import type { Breadcrumb, BreadcrumbHint } from '@sentry/react'
import type { BeforeFetchBreadcrumb } from '@sketch/utils'
import type { OperationsMap } from '@sketch/gql-types'
import { set } from 'lodash'

const REDACTED = '[Redacted]'

/**
 * Type-safe helper that ensures:
 * - Operation names are valid GraphQL operations from the schema
 * - Field names are valid variable keys for each operation
 *
 * For nested fields (e.g. inside an "input" wrapper), add them
 * directly using dot-notation paths via the Leaves utility type.
 */
type TypedSensitiveFieldsMap = {
  [K in keyof OperationsMap]?: Array<
    Leaves<OperationsMap[K]['variablesType']> & string
  >
}

/**
 * GraphQL operations with sensitive variables that should be
 * redacted from Sentry breadcrumbs.
 */
const sensitiveFields = {
  // Authentication
  signIn: ['password'],
  signInWithSso: ['exchangeToken'],
  signInWithSocial: ['exchangeToken'],
  signInAndDisableMfa: ['password', 'disableMfaToken'],
  linkRegularAccount: ['password'],
  linkRegularAccountWithMfa: ['mfa_token', 'totp', 'recovery_code'],
  revokeOAuth: ['token'],

  // MFA
  validateMfaTotp: ['token', 'totp'],
  validateMfaRecoveryCode: ['token', 'recoveryCode'],
  confirmMfaSetup: ['totp'],
  disableMfa: ['password'],
  generateMfaRecoveryCodes: ['password'],

  // User account
  userPasswordUpdate: ['currentPassword', 'newPassword'],
  userPasswordReset: ['token', 'newPassword'],
  userDelete: ['password'],
  emailUpdate: ['password'],
  emailVerify: ['token'],

  // Preview password protection
  getPreview: ['password'],
  getPreviewDetails: ['password'],
  getPreviewPages: ['password'],
  getPreviewPrototypes: ['password'],
  getPreviewPage: ['password'],
  createPreview: ['password'],
  updatePreview: ['password'],

  // Projects and shares
  getProjectMembershipByToken: ['token'],
  getShareMembership: ['token'],
  acceptShareMembershipInvitation: ['token'],

  // Workspace
  deleteWorkspace: ['password'],
  acceptWorkspaceInvite: ['membershipToken'],
  acceptPartnershipInvite: ['membershipToken'],
  getPendingWorkspaceMembership: ['membershipToken'],
  getPublicWorkspaceFromToken: ['inviteToken'],

  // Billing
  subscribeCustomerToPlan: ['input.paymentSource'],
  createWorkspace: ['input.paymentMethod'],
} satisfies TypedSensitiveFieldsMap

/**
 * Direct REST auth calls with sensitive body fields.
 * These bypass the GraphQL client and are matched by URL endpoint.
 */
const sensitiveEndpoints: Record<
  string,
  { operation: string; fields: string[] }
> = {
  // Authentication (longer paths first to avoid partial matches)
  '/oauth/social/token': {
    operation: 'signInWithSocial',
    fields: ['token'],
  },
  '/oauth/token': {
    operation: 'signIn',
    fields: ['password', 'access_token', 'refresh_token'],
  },
  '/sso/token': {
    operation: 'signInWithSso',
    fields: ['token'],
  },
  '/oauth/revoke': {
    operation: 'revokeOAuth',
    fields: ['token'],
  },

  // MFA
  '/oauth/mfa_totp': {
    operation: 'validateMfa',
    fields: ['mfa_token', 'totp', 'recovery_code'],
  },
  '/oauth/disable_mfa': {
    operation: 'signInAndDisableMfa',
    fields: ['password', 'disable_mfa_token'],
  },
}

const safeParse = (value: string): Record<string, unknown> | null => {
  try {
    return JSON.parse(value)
  } catch {
    return null
  }
}

const redactFields = (
  body: Record<string, unknown>,
  fields: string[]
): Record<string, unknown> => {
  const redacted: Record<string, unknown> = structuredClone(body)

  for (const field of fields) {
    set(redacted, field, REDACTED)
  }

  return redacted
}

/**
 * Sanitizes fetch breadcrumbs before they're sent to Sentry.
 * Handles two cases:
 * - GraphQL requests: matched by operationName, fields from sensitiveFields map
 * - Direct REST auth requests: matched by URL, fields from sensitiveEndpoints map
 */
export const beforeFetchBreadcrumb: BeforeFetchBreadcrumb = (
  breadcrumb: Breadcrumb,
  hint?: BreadcrumbHint
) => {
  if (!hint?.input?.[1]?.body) {
    return breadcrumb
  }

  const rawBody = hint.input[1].body
  const parsedBody = safeParse(rawBody)

  if (!parsedBody) {
    return breadcrumb
  }

  // GraphQL request — has operationName
  if (parsedBody.operationName) {
    const operation = parsedBody.operationName as string
    const variables = parsedBody.variables as Record<string, unknown> | null
    const fields = sensitiveFields[operation as keyof typeof sensitiveFields]
    const error = hint.data instanceof Error ? hint.data : null

    const sanitizedVariables =
      variables && fields
        ? redactFields(variables, fields as string[])
        : variables

    return {
      ...breadcrumb,
      data: {
        ...breadcrumb.data,
        operation,
        variables: JSON.stringify(sanitizedVariables),
        error,
      },
    }
  }

  // Direct REST request — match by URL
  const url = breadcrumb.data?.url as string | undefined

  const endpoint = Object.entries(sensitiveEndpoints).find(
    ([pathname]) => url && url.includes(pathname)
  )?.[1]

  if (endpoint) {
    const sanitizedBody = redactFields(parsedBody, endpoint.fields)

    return {
      ...breadcrumb,
      data: {
        ...breadcrumb.data,
        operation: endpoint.operation,
        body: JSON.stringify(sanitizedBody),
      },
    }
  }

  return breadcrumb
}
